import React, { useState, useRef } from 'react';
import {
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Select,
  MenuItem,
  Grid,
  Box,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import Papa from 'papaparse';
import AdminInventoryList from '../../utilities/api/inventory/AdminInventoryList';
import InventoryCategoryAPIs from '../../utilities/api/category/AdminInventoryCategoryList';
import InventoryLocationAPIs from '../../utilities/api/location/AdminLocationList';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoIcon from '@mui/icons-material/Info';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DownloadIcon from '@mui/icons-material/Download';
import { extractBulkInventoryValidationErrorMessage } from '../../utils/helper';
import { InfoOutlined } from '@mui/icons-material';

// Inventory CSV Import Schema Constants
const inventorySchemaFields = [
  'name',
  'upc',
  'manufacturer',
  'description',
  'location',
  'stock_available',
  'stock_total',
  'instances',
  'stock_unit',
  'purchase_cost',
  'purchase_date',
  'warranty_expiration',
  'category',
  'group',
  'availability',
  'status',
  'supervised',
  'tags',
];

const inventorySchemaDescription = {
  name: { desc: 'Inventory item name', mandatory: true, options: 'Text' },
  upc: { desc: 'Universal Product Code', mandatory: false, options: 'Number' },
  manufacturer: {
    desc: 'Manufacturer name',
    mandatory: false,
    options: 'Text',
  },
  description: { desc: 'Item description', mandatory: false, options: 'Text' },
  location: {
    desc: 'Storage location',
    mandatory: false,
    options: 'Location name',
  },
  stock_available: {
    desc: 'Number of items available',
    mandatory: true,
    options: 'Number',
  },
  stock_total: {
    desc: 'Total number of items',
    mandatory: true,
    options: 'Number',
  },
  instances: {
    desc: 'Number of instances',
    mandatory: true,
    options: 'Number',
  },
  stock_unit: {
    desc: 'Unit of stock (e.g., pcs, kg)',
    mandatory: false,
    options: 'Text',
  },
  purchase_cost: { desc: 'Cost per unit', mandatory: false, options: 'Number' },
  purchase_date: {
    desc: 'Purchase date',
    mandatory: false,
    options: 'YYYY-MM-DD',
  },
  warranty_expiration: {
    desc: 'Warranty expiration date',
    mandatory: false,
    options: 'YYYY-MM-DD',
  },
  category: {
    desc: 'Inventory category',
    mandatory: false,
    options: 'Category name',
  },
  group: { desc: 'Inventory group', mandatory: false, options: 'Group name' },
  availability: {
    desc: 'Availability level',
    mandatory: false,
    options: 'Moderate, Very Low, Low, High, Always',
  },
  status: {
    desc: 'Inventory status',
    mandatory: false,
    options: 'Operational, Under Maintenance, Out of Service',
  },
  supervised: {
    desc: 'Requires supervision (TRUE/FALSE)',
    mandatory: false,
    options: 'TRUE or FALSE',
  },
  tags: { desc: 'Inventory tags', mandatory: false, options: 'Tags List' },
};

const availabilityMapping = {
  moderate: '0',
  'very low': '1',
  low: '2',
  high: '3',
  always: '4',
};

const statusMapping = {
  operational: '0',
  'under maintenance': '1',
  'out of service': '2',
};

const InventoryImport = ({
  user,
  categories,
  locations,
  groups,
  onImportComplete,
}) => {
  // State for CSV Import
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isMappingDialogOpen, setIsMappingDialogOpen] = useState(false);
  const [isResultDialogOpen, setIsResultDialogOpen] = useState(false);
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false);
  const [csvData, setCsvData] = useState({ headers: [], rows: [] });
  const [mapping, setMapping] = useState({});
  const [errorEntries, setErrorEntries] = useState([]);
  const [successfulImports, setSuccessfulImports] = useState(0);
  const [totalEntries, setTotalEntries] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [localCategories, setLocalCategories] = useState([]);
  const [localLocations, setLocalLocations] = useState([]);
  const fileInputRef = useRef(null);

  // Initialize local categories and locations from props
  React.useEffect(() => {
    if (categories && Array.isArray(categories)) {
      setLocalCategories(categories.map(cat => cat.name || cat));
    }
    if (locations && Array.isArray(locations)) {
      setLocalLocations(locations.map(loc => loc.name || loc));
    }
  }, [categories, locations]);

  console.log('Locations :- ', locations);

  const tagMapping = {
    Free: '0',
    Issue: '1',
    Purchase: '2',
    Exhaustible: '3',
    NonExhaustible: '4',
    InLabOnly: '5',
    HighValue: '6',
  };

  // Import process steps
  const steps = ['Select CSV File', 'Map Fields', 'Import Results'];

  const handleOpenImportDialog = () => {
    setIsImportDialogOpen(true);
    setActiveStep(0);
  };

  const handleCloseImportDialog = () => {
    setIsImportDialogOpen(false);
    setCsvData({ headers: [], rows: [] });
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = event => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      Papa.parse(file, {
        header: false,
        skipEmptyLines: true,
        complete: results => {
          if (results.data && results.data.length >= 2) {
            const headers = results.data[0].map(header =>
              header.trim().toLowerCase()
            );

            // Remove empty rows (all cells blank)
            const cleanedRows = results.data
              .slice(1)
              .map(row => row.map(cell => cell.trim()))
              .filter(row => row.some(cell => cell !== ''));

            setCsvData({ headers, rows: cleanedRows });

            const newInitialMapping = {};
            inventorySchemaFields.forEach(field => {
              const foundHeader = headers.find(h =>
                h.replace(/\s+/g, '_').includes(field.toLowerCase())
              );
              newInitialMapping[field] = foundHeader || '';
            });

            setMapping(newInitialMapping);
            setIsImportDialogOpen(false);
            setIsMappingDialogOpen(true);
            setActiveStep(1);
          } else {
            toast.error(
              'CSV file must contain headers and at least one data row.'
            );
          }
        },
        error: error => {
          toast.error('Error parsing CSV file: ' + error.message);
        },
      });
    } else {
      toast.error('Please upload a valid .csv file.');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleMappingSave = finalMapping => {
    setMapping(finalMapping);
    setIsMappingDialogOpen(false);
    setActiveStep(2);
    handleBulkImportInventory(finalMapping);
  };

  const findCategoryByName = name => {
    if (!name || !categories) return null;
    const foundCategory = categories.find(
      c => c.name && c.name.toLowerCase() === name.toLowerCase()
    );
    return foundCategory ? foundCategory.id : null;
  };

  const findLocationByName = name => {
    if (!name || !locations) return null;
    const foundLocation = locations.find(
      l => l.name && l.name.toLowerCase() === name.toLowerCase()
    );
    return foundLocation ? foundLocation.id : null;
  };

  const findGroupByName = name => {
    if (!name || !groups) return null;
    const foundGroup = groups.find(
      g => g.name && g.name.toLowerCase() === name.toLowerCase()
    );
    return foundGroup ? foundGroup.id : null;
  };

  const validateDate = dateString => {
    if (!dateString) return null;
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(dateString)) {
      throw new Error(`Date must be in YYYY-MM-DD format`);
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      throw new Error(`Invalid date`);
    }
    return dateString;
  };

  const handleBulkImportInventory = async currentMapping => {
    if (!user || !user.access) {
      toast.error('Authentication token not found. Please log in again.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadingMessage('Starting inventory import...');
    setErrorEntries([]);
    setSuccessfulImports(0);
    setTotalEntries(csvData.rows.length);

    let currentSuccessCount = 0;
    const errors = [];

    for (let i = 0; i < csvData.rows.length; i++) {
      const row = csvData.rows[i];
      const entry = {};

      setLoadingMessage(`Processing row ${i + 1}/${csvData.rows.length}...`);

      try {
        inventorySchemaFields.forEach(field => {
          const csvHeader = currentMapping[field];
          const headerIndex = csvData.headers.indexOf(csvHeader);
          if (
            csvHeader &&
            headerIndex !== -1 &&
            row[headerIndex] !== undefined &&
            row[headerIndex] !== ''
          ) {
            entry[field] = row[headerIndex];
          } else if (inventorySchemaDescription[field]?.mandatory) {
            throw new Error(
              `Mandatory field '${
                inventorySchemaDescription[field].desc
              }' is missing or not mapped for row ${i + 1}.`
            );
          }
        });

        // Handle Category - create new category if it doesn't exist
        if (
          entry.category &&
          entry.category.trim() !== '' &&
          !localCategories.includes(entry.category)
        ) {
          try {
            await InventoryCategoryAPIs.CategoryListPost(
              entry.category,
              user.access
            );
            setLocalCategories(prevCategories => [
              ...prevCategories,
              entry.category,
            ]);
            console.log(`Created new category: ${entry.category}`);
          } catch (error) {
            if (
              error.response?.status === 400 ||
              error.response?.status === 409
            ) {
              // Ignore error if category already exists
              console.log(
                `Category '${entry.category}' already exists, ignoring error.`
              );
              // Add to local categories list to avoid future API calls
              setLocalCategories(prevCategories => [
                ...prevCategories,
                entry.category,
              ]);
            } else {
              throw new Error(
                `Failed to create category '${entry.category}': ${error.message}`
              );
            }
          }
        }

        // Handle Location - create new location if it doesn't exist
        if (
          entry.location &&
          entry.location.trim() !== '' &&
          !localLocations.includes(entry.location)
        ) {
          try {
            await InventoryLocationAPIs.LocationListPost(
              entry.location,
              user.access
            );
            setLocalLocations(prevLocations => [
              ...prevLocations,
              entry.location,
            ]);
            console.log(`Created new location: ${entry.location}`);
          } catch (error) {
            if (
              error.response?.status === 400 ||
              error.response?.status === 409
            ) {
              // Ignore error if location already exists
              console.log(
                `Location '${entry.location}' already exists, ignoring error.`
              );
              // Add to local locations list to avoid future API calls
              setLocalLocations(prevLocations => [
                ...prevLocations,
                entry.location,
              ]);
            } else {
              throw new Error(
                `Failed to create location '${entry.location}': ${error.message}`
              );
            }
          }
        }

        // Handle supervised boolean conversion
        if (typeof entry.supervised === 'string') {
          entry.supervised = entry.supervised.toLowerCase() === 'true';
        } else if (typeof entry.supervised === 'boolean') {
          // already boolean
        } else {
          entry.supervised = false; // default to false if not specified
        }

        // Validate numeric fields
        if (entry.upc && isNaN(Number(entry.upc))) {
          throw new Error(`UPC must be a number for row ${i + 1}.`);
        }
        if (entry.instances && isNaN(Number(entry.instances))) {
          throw new Error(`Instances must be a number for row ${i + 1}.`);
        }
        if (entry.purchase_cost && isNaN(Number(entry.purchase_cost))) {
          throw new Error(`Purchase cost must be a number for row ${i + 1}.`);
        }
        if (isNaN(entry.stock_available)) {
          throw new Error(`Stock Available must be a number for row ${i + 1}.`);
        }
        if (isNaN(entry.stock_total)) {
          throw new Error(`Stock Total must be a number for row ${i + 1}.`);
        }

        // Handle availability mapping - convert text to numeric value
        if (entry.availability) {
          const availabilityLower = entry.availability.toLowerCase();
          const availabilityValue = availabilityMapping[availabilityLower];
          if (availabilityValue !== undefined) {
            entry.availability = availabilityValue;
          } else {
            throw new Error(
              `Invalid availability '${entry.availability}' for row ${
                i + 1
              }. Valid options are: ${Object.keys(availabilityMapping).join(', ')}`
            );
          }
        }

        // Handle status mapping - convert text to numeric value
        if (entry.status) {
          const statusLower = entry.status.toLowerCase();
          const statusValue = statusMapping[statusLower];
          if (statusValue !== undefined) {
            entry.status = statusValue;
          } else {
            throw new Error(
              `Invalid status '${entry.status}' for row ${i + 1}. Valid options are: ${Object.keys(
                statusMapping
              ).join(', ')}`
            );
          }
        }

        // Data transformation and validation
        // const categoryId = findCategoryByName(entry.category);
        // const locationId = findLocationByName(entry.location);
        const groupId = findGroupByName(entry.group);
        if (entry.group && !groupId) {
          throw new Error(`Group '${entry.group}' not found for row ${i + 1}.`);
        }

        // Date validation
        if (entry.purchase_date) {
          entry.purchase_date = validateDate(entry.purchase_date);
        }
        if (entry.warranty_expiration) {
          entry.warranty_expiration = validateDate(entry.warranty_expiration);
        }

        // Construct FormData payload for API
        const formData = new FormData();
        formData.append('name', entry.name || '');
        formData.append('upc', entry.upc || '');
        formData.append('manufacturer', entry.manufacturer || '');
        formData.append('description', entry.description || '');
        formData.append('location', entry.location || '');
        formData.append(
          'stock_available',
          entry.stock_available.toString() || '0'
        );
        formData.append('stock_total', entry.stock_total.toString() || '0');
        formData.append('instances', entry.instances || '1');
        formData.append('stock_unit', entry.stock_unit || '');
        formData.append('purchase_cost', entry.purchase_cost.toString() || '0');
        formData.append('purchase_date', entry.purchase_date || '');
        formData.append('warranty_expiration', entry.warranty_expiration || '');
        formData.append('category', entry.category || '');
        formData.append('group', entry.group || '');
        formData.append('is_Hidden', 'false');
        formData.append('supervised', entry.supervised || false);

        // Append mapped availability and status values
        if (entry.availability) {
          formData.append('availability1', entry.availability);
        }
        if (entry.status) {
          formData.append('status1', entry.status);
        }

        // Handle tags array - convert tag names to IDs
        if (entry.tags) {
          const tagsArray = entry.tags
            .split(',')
            .map(tag => tag.trim())
            .filter(tag => tag);
          tagsArray.forEach(tag => {
            const tagId = tagMapping[tag];
            if (tagId) {
              formData.append('tags', tagId);
            } else {
              throw new Error(
                `Invalid tag '${tag}' found for row ${i + 1}. Valid tags are: ${Object.keys(
                  tagMapping
                ).join(', ')}`
              );
            }
          });
        }

        if (parseInt(entry.stock_available) > parseInt(entry.stock_total))
          throw new Error(
            `Stock Available cannot be greater than Stock Total for row ${i + 1}.`
          );

        await AdminInventoryList.AddInventory(formData, user.access);
        currentSuccessCount++;
        setSuccessfulImports(prev => prev + 1);
      } catch (err) {
        console.error('Error processing row:', i + 1, err, entry);
        // Extract proper Axios validation error
        const errorMessage = extractBulkInventoryValidationErrorMessage(err);
        errors.push({
          row: i + 1,
          data: {
            ...entry,
            category:
              row[csvData.headers.indexOf(currentMapping['category'])] || 'N/A',
            location:
              row[csvData.headers.indexOf(currentMapping['location'])] || 'N/A',
          },
          error: errorMessage,
        });
      }
    }

    setErrorEntries(errors);
    setIsLoading(false);
    setLoadingMessage('');
    setIsResultDialogOpen(true);

    if (onImportComplete && typeof onImportComplete === 'function') {
      onImportComplete();
    }
  };

  // Calculate completion status for each required field
  const getMappingCompletionStatus = () => {
    let mandatoryFieldsMapped = 0;
    let totalMandatoryFields = 0;

    Object.keys(inventorySchemaDescription).forEach(field => {
      if (inventorySchemaDescription[field].mandatory) {
        totalMandatoryFields++;
        if (mapping[field] && mapping[field] !== '') {
          mandatoryFieldsMapped++;
        }
      }
    });

    return {
      complete: mandatoryFieldsMapped === totalMandatoryFields,
      mandatoryFieldsMapped,
      totalMandatoryFields,
      percentage:
        totalMandatoryFields > 0
          ? (mandatoryFieldsMapped / totalMandatoryFields) * 100
          : 0,
    };
  };

  const downloadSampleCSV = () => {
    const headers =
      'name,upc,manufacturer,description,location,stock_available,stock_total,instances,stock_unit,purchase_cost,purchase_date,warranty_expiration,category,group,availability,status,supervised,tags';
    const sampleData =
      'Proximity Sensor D80NK,00095,3M,Protective eyewear for lab use,A2.1,20,25,5,pcs,15,2024-01-15,2026-01-15,Electronics,,High,Operational,TRUE,"free"';

    const csvContent = `${headers}\n${sampleData}`;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'inventory_import_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* Import Button */}
      <Button
        variant="contained"
        color="primary"
        onClick={handleOpenImportDialog}
        startIcon={<CloudUploadIcon />}
        sx={{
          display: 'flex',
          flex: { xs: 1, md: 'none' },
          minHeight: '54px',
          flexDirection: 'row',
          padding: '12px 16px',
          borderRadius: '12px',
          backgroundColor: '#FAFAFA',
          color: '#000',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #E6E6E6',
          gap: '8px',
          textTransform: 'none',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginLeft: '16px !important',
          '& .MuiButton-startIcon': {
            margin: 0,
          },
          '&:hover': {
            backgroundColor: '#FAFAFA',
            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
            borderColor: '#E6E6E6',
          },
        }}
      >
        <Typography variant="subtitle1" display={{ xs: 'none', lg: 'block' }}>
          Import Inventory
        </Typography>
      </Button>

      {/* Import Dialog with Stepper */}
      <Dialog
        open={isImportDialogOpen}
        onClose={() => {
          if (!isLoading) handleCloseImportDialog();
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Typography variant="h6" fontWeight={500}>
            Import Inventory from CSV
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 2, pt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '5px' }}>
            <InfoOutlined fontSize="small" color="info" sx={{ mt: '2px' }} />
            <Typography variant="body1" color="info.main">
              We recommend using only the system-generated CSV template for bulk
              inventory import. Uploading custom or modified CSV files may cause
              errors or data mismatches. Ensure that you download the latest
              template before each upload.
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} alternativeLabel sx={{ my: 3 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="body1" sx={{ mr: 1 }}>
              Upload a .csv file with inventory data
            </Typography>
            <Tooltip title="View Schema Information">
              <IconButton
                size="small"
                onClick={() => setIsInfoDialogOpen(true)}
                color="primary"
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              backgroundColor: '#f5f9ff',
              borderRadius: '8px',
              borderStyle: 'dashed',
              borderWidth: '1px',
              borderColor: '#2196f3',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: '#edf5fe',
              },
            }}
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
          >
            <CloudUploadIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body1" gutterBottom>
              Click to browse or drag & drop your CSV file here
            </Typography>
            <Typography variant="caption" color="textSecondary">
              File should contain headers and inventory data
            </Typography>
            <input
              type="file"
              accept=".csv"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </Paper>

          <Box
            sx={{
              mt: 2,
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: '12px',
            }}
          >
            <Chip
              icon={<InfoIcon />}
              label="Need a template?"
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => setIsInfoDialogOpen(true)}
              sx={{ mr: 1 }}
            />
            <Chip
              icon={<DownloadIcon />}
              label="Download CSV Template"
              variant="outlined"
              color="secondary"
              size="small"
              onClick={downloadSampleCSV}
              sx={{ mr: 1 }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={handleCloseImportDialog}
            variant="outlined"
            color="inherit"
            disabled={isLoading}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mapping Dialog */}
      <Dialog
        open={isMappingDialogOpen}
        onClose={() => !isLoading && setIsMappingDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" fontWeight={500}>
              Map CSV Columns to Inventory Fields
            </Typography>
            <Chip
              label={`${getMappingCompletionStatus().mandatoryFieldsMapped}/${
                getMappingCompletionStatus().totalMandatoryFields
              } Required Fields Mapped`}
              color={
                getMappingCompletionStatus().complete ? 'success' : 'warning'
              }
              size="small"
            />
          </Box>
        </DialogTitle>

        <DialogContent sx={{ mt: 2, pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="body1">
              Match your CSV column headers to the required inventory fields
            </Typography>
            <Tooltip title="View Schema Information">
              <IconButton
                size="small"
                onClick={() => setIsInfoDialogOpen(true)}
                color="primary"
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: '#f5f9ff',
              borderRadius: '8px',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Preview: {csvData.rows.length} rows found in CSV
            </Typography>
            <Box
              sx={{
                maxHeight: '100px',
                overflow: 'auto',
                backgroundColor: '#fff',
                p: 1,
                borderRadius: '4px',
                border: '1px solid #e0e0e0',
                fontSize: '0.875rem',
              }}
            >
              <Typography
                component="div"
                variant="body2"
                sx={{ fontFamily: 'monospace', whiteSpace: 'pre' }}
              >
                {csvData.headers.join(', ')}
              </Typography>
              <Divider sx={{ my: 0.5 }} />
              {csvData.rows.slice(0, 2).map((row, idx) => (
                <Typography
                  key={idx}
                  component="div"
                  variant="body2"
                  sx={{ fontFamily: 'monospace', whiteSpace: 'pre' }}
                >
                  {row.join(', ')}
                </Typography>
              ))}
              {csvData.rows.length > 2 && (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                >
                  ... and {csvData.rows.length - 2} more rows
                </Typography>
              )}
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: '8px',
              border: '1px solid #eee',
              maxHeight: '50vh',
              overflow: 'auto',
            }}
          >
            <Grid container spacing={2}>
              <Grid
                item
                xs={12}
                sx={{ borderBottom: '1px solid #eee', pb: 1, mb: 1 }}
              >
                <Grid container>
                  <Grid item xs={5}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Inventory Field
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      CSV Header
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              {inventorySchemaFields.map(field => (
                <Grid
                  item
                  xs={12}
                  key={field}
                  sx={{
                    py: 0.5,
                    backgroundColor: inventorySchemaDescription[field]
                      ?.mandatory
                      ? '#f8f8f8'
                      : 'transparent',
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={5}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {inventorySchemaDescription[field]?.mandatory && (
                          <Typography sx={{ color: 'error.main', mr: 0.5 }}>
                            *
                          </Typography>
                        )}
                        <Typography>
                          {inventorySchemaDescription[field]?.desc || field}
                        </Typography>
                        <Tooltip
                          title={
                            inventorySchemaDescription[field]?.options || ''
                          }
                        >
                          <IconButton size="small">
                            <InfoIcon fontSize="small" color="action" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Grid>
                    <Grid item xs={7}>
                      <FormControl
                        fullWidth
                        size="small"
                        sx={{ backgroundColor: 'white' }}
                      >
                        <Select
                          value={mapping[field] || ''}
                          onChange={e =>
                            setMapping(prev => ({
                              ...prev,
                              [field]: e.target.value,
                            }))
                          }
                          displayEmpty
                          sx={{ borderRadius: '8px' }}
                          startAdornment={
                            mapping[field] ? (
                              <CheckCircleOutlineIcon
                                fontSize="small"
                                color="success"
                                sx={{ mr: 1, ml: 1 }}
                              />
                            ) : inventorySchemaDescription[field]?.mandatory ? (
                              <ErrorOutlineIcon
                                fontSize="small"
                                color="error"
                                sx={{ mr: 1, ml: 1 }}
                              />
                            ) : null
                          }
                        >
                          <MenuItem value="">
                            <em>Not mapped</em>
                          </MenuItem>
                          {csvData.headers.map(header => (
                            <MenuItem
                              key={header}
                              value={header}
                              disabled={
                                Object.values(mapping).includes(header) &&
                                mapping[field] !== header
                              }
                            >
                              {header}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            borderTop: '1px solid #eee',
            p: 2,
            justifyContent: 'space-between',
          }}
        >
          <Button
            onClick={() => {
              setIsMappingDialogOpen(false);
              setIsImportDialogOpen(true);
              setActiveStep(0);
            }}
            startIcon={<ArrowBackIcon />}
            disabled={isLoading}
          >
            Back
          </Button>
          <Box>
            <Button
              onClick={() => setIsMappingDialogOpen(false)}
              color="inherit"
              sx={{ mr: 1 }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleMappingSave(mapping)}
              variant="contained"
              color="primary"
              disabled={!getMappingCompletionStatus().complete || isLoading}
              endIcon={<ArrowForwardIcon />}
            >
              Import Inventory
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Result Dialog */}
      <Dialog
        open={isResultDialogOpen}
        onClose={() => setIsResultDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Typography variant="h6" fontWeight={500}>
            Import Results
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ mt: 2, pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              borderRadius: '8px',
              backgroundColor: successfulImports > 0 ? '#f0f7f0' : '#fff',
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mb: 2,
              }}
            >
              {successfulImports > 0 ? (
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 48, color: 'success.main', mr: 2 }}
                />
              ) : (
                <ErrorOutlineIcon
                  sx={{ fontSize: 48, color: 'warning.main', mr: 2 }}
                />
              )}
              <Typography variant="h5" component="div">
                {successfulImports} of {totalEntries} inventory items imported
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Chip
                label={`${successfulImports} Successful`}
                color="success"
                variant="outlined"
                icon={<CheckCircleOutlineIcon />}
              />
              {errorEntries.length > 0 && (
                <Chip
                  label={`${errorEntries.length} Failed`}
                  color="error"
                  variant="outlined"
                  icon={<ErrorOutlineIcon />}
                />
              )}
            </Box>
          </Paper>

          {errorEntries.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
              <Typography
                variant="subtitle1"
                color="error"
                fontWeight={500}
                gutterBottom
              >
                Errors ({errorEntries.length})
              </Typography>
              <Box
                sx={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #eee',
                  borderRadius: '4px',
                }}
              >
                {errorEntries.map(err => (
                  <Box
                    key={err.row}
                    sx={{
                      p: 1.5,
                      borderBottom: '1px solid #eee',
                      '&:last-child': { borderBottom: 'none' },
                      backgroundColor: '#fff5f5',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.5,
                      }}
                    >
                      <Typography variant="subtitle2">
                        Row {err.row}: {err.data.name || 'Untitled Item'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Category: {err.data.category || 'N/A'}
                      </Typography>
                    </Box>
                    <Typography
                      color="error.main"
                      variant="body2"
                      sx={{ fontWeight: 500 }}
                    >
                      {err.error}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={() => setIsResultDialogOpen(false)}
            variant="contained"
            color="primary"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schema Info Dialog */}
      <Dialog
        open={isInfoDialogOpen}
        onClose={() => setIsInfoDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: { borderRadius: '10px' },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
          <Typography variant="h6" fontWeight={500}>
            Inventory CSV Schema Information
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography gutterBottom>
              The following fields are expected for inventory import. Fields
              marked with an asterisk (*) are mandatory.
            </Typography>

            <Paper
              variant="outlined"
              sx={{ mt: 2, borderRadius: '8px', overflow: 'hidden' }}
            >
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: '#f5f5f5',
                  borderBottom: '1px solid #ddd',
                }}
              >
                <Grid container spacing={1}>
                  <Grid item xs={3}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Field Name
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Description
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Expected Format / Options
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
                {inventorySchemaFields.map((field, index) => (
                  <Box
                    key={field}
                    sx={{
                      p: 1.5,
                      borderBottom:
                        index < inventorySchemaFields.length - 1
                          ? '1px solid #eee'
                          : 'none',
                      backgroundColor: inventorySchemaDescription[field]
                        ?.mandatory
                        ? '#f8f8f8'
                        : 'transparent',
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={3}>
                        <Typography
                          sx={{
                            fontWeight: inventorySchemaDescription[field]
                              ?.mandatory
                              ? 'bold'
                              : 'normal',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {field}
                          {inventorySchemaDescription[field]?.mandatory && (
                            <Box
                              component="span"
                              sx={{ color: 'error.main', ml: 0.5 }}
                            >
                              *
                            </Box>
                          )}
                        </Typography>
                      </Grid>
                      <Grid item xs={5}>
                        <Typography>
                          {inventorySchemaDescription[field]?.desc}
                        </Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <Typography>
                          {inventorySchemaDescription[field]?.options}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                mt: 3,
                p: 2,
                borderRadius: '8px',
                backgroundColor: '#f5f9ff',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight={500}>
                  Sample CSV Format
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={downloadSampleCSV}
                  sx={{
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 500,
                  }}
                >
                  Download Template
                </Button>
              </Box>
              <Box
                sx={{
                  p: 1,
                  backgroundColor: '#fff',
                  borderRadius: '4px',
                  border: '1px solid #e0e0e0',
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre', fontFamily: 'monospace' }}
                >
                  name,upc,stock_available,stock_total,stock_unit,category,location,group,manufacturer,purchase_cost,purchase_date,warranty_expiration,description
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ whiteSpace: 'pre', fontFamily: 'monospace' }}
                >
                  Safety Goggles,987654321,20,25,pcs,Safety Equipment,Storage
                  Room A,Lab Supplies,3M,15.99,2024-01-15,2026-01-15,Protective
                  eyewear for lab use
                </Typography>
              </Box>
            </Paper>
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid #eee', p: 2 }}>
          <Button
            onClick={() => setIsInfoDialogOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Dialog */}
      <Dialog
        open={isLoading}
        PaperProps={{
          style: {
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
            backdropFilter: 'blur(4px)',
            borderRadius: '12px',
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: 'center',
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '250px',
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {loadingMessage}
          </Typography>
          {totalEntries > 0 && (
            <Box sx={{ width: '100%', mt: 2 }}>
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={(successfulImports / totalEntries) * 100}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {Math.round((successfulImports / totalEntries) * 100)}%
                  </Typography>
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {successfulImports}/{totalEntries} processed
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InventoryImport;
